import { useState, useEffect, useMemo, useCallback } from "react";
import { Button, Modal, Input, Spin, message, Tooltip } from "antd";
import { Edit2, Plus, X } from "react-feather";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  arrayMove,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import styles from "./stylesLiveSportsV2.module.css";
// Import AddNewIpPopup styles for consistent modal styling
import "../../../WhiteList/AddNewIpPopup/stylesAddNewIpPopup.css";
// Import API functions
import { API_ROUTES } from "../../../../api/ApiRoutes";
import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import {
  getApiErrorMessage,
  getApiSuccessMessage,
} from "../../../../utils/ErrorUtils";
import SortingTable from "../SortingTable/SortingTable";
import ClipPath from "../../../../assets/icons/ic_clip-path.svg";
import { useSportsbookFm } from "../SportsbookFmContext";
import { RenameEntityModal } from "../Translations/RenameEntityModal";

// Sortable Sport Pill Component
const SortableSportPill = ({
  sport,
  isActive,
  onClick,
  disabled,
  onRename,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sport.entity_id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: disabled ? "default" : "grab",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...(disabled ? {} : listeners)}
      className={`${styles.emSportPill} ${isActive ? styles.active : ""}`}
      onClick={onClick}
    >
      {!disabled && (
        <img className="drag-handle" src={ClipPath} alt={AppStrings.DRAG} />
      )}
      <div className={styles.sportNameContainer}>
        <span>{sport.display_name}</span>
        <Tooltip title={AppStrings.RENAME}>
          <Edit2
            data-testid={`rename-sport-${sport.entity_id}`}
            className={styles.editIcon}
            onClick={(e) => {
              e.stopPropagation();
              onRename(sport);
            }}
          />
        </Tooltip>
      </div>
    </div>
  );
};

const LiveSportsV2 = () => {
  const { canEdit } = useSportsbookFm();
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeSport, setActiveSport] = useState(null);
  const activePreMatchLive = "LIVE";

  // Rename state
  const [isRenameModalVisible, setIsRenameModalVisible] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameLoading, setRenameLoading] = useState(false);
  const [renameKind, setRenameKind] = useState(null);

  // Get token and create API instance
  const token = useMemo(() => JSON.parse(localStorage.getItem("token")), []);
  const PrivateAdminPanelInstance = useMemo(
    () => createPrivateAdminPanelInstance(token?.AccessToken),
    [token?.AccessToken],
  );

  const [popularTabs, setPopularTabs] = useState([]);
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [sports, setSports] = useState([]);
  const [sportsLoading, setSportsLoading] = useState(false);

  // DnD sensors for sports reordering
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  // API Functions
  const fetchSports = useCallback(async () => {
    setSportsLoading(true);
    try {
      const response = await PrivateAdminPanelInstance.get(
        API_ROUTES.GET_LIVE_SPORTS_V2,
      );
      const sportsData = response.data.data.items;

      // Sort by display_order
      sportsData.sort(
        (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0),
      );

      setSports(sportsData);

      // Set the first sport as active
      if (sportsData?.length > 0) {
        setActiveSport(sportsData[0].sport_id);
      }
    } catch (error) {
      console.error("Error fetching sports:", error);
      message.error(
        getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED),
      );
    } finally {
      setSportsLoading(false);
    }
  }, [PrivateAdminPanelInstance]);

  const handleSportsDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sports.findIndex((s) => s.entity_id === active.id);
    const newIndex = sports.findIndex((s) => s.entity_id === over.id);

    const reorderedSports = arrayMove(sports, oldIndex, newIndex).map(
      (sport, index) => ({ ...sport, display_order: index }),
    );

    const previousState = [...sports];
    setSports(reorderedSports);

    try {
      setIsTableLoading(true);
      const orderedIds = reorderedSports.map((s) => s.entity_id);
      const response = await PrivateAdminPanelInstance.patch(
        API_ROUTES.REORDER_LIVE_SPORTS_V2,
        { ordered_ids: orderedIds },
      );
      message.success(
        getApiSuccessMessage(
          response,
          AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED,
        ),
      );
    } catch (error) {
      console.error("Error reordering sports:", error);
      message.error(
        getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED),
      );
      setSports(previousState);
    } finally {
      setIsTableLoading(false);
    }
  };

  const fetchLiveSportsV2 = useCallback(async () => {
    if (!activeSport) return;

    try {
      setIsTableLoading(true);
      const url = API_ROUTES.GET_TOP_LEAGUES_PER_SPORT.replace(
        "{sport_id}",
        activeSport,
      ).replace("{event_type}", activePreMatchLive);

      const response = await PrivateAdminPanelInstance.get(url);

      if (response.data.success) {
        const topLeaguesData = response.data.data.items.map((item) => ({
          id: item?.entity_id || "",
          entity_id: item?.entity_id || "",
          item_id: item?.item_id || "",
          ename: item?.display_name || "",
          order: item?.display_order?.toString() || "",
          sport_id: item?.sport_id || "",
          event_type: item?.event_type || "",
          is_active: item?.is_active ?? true,
        }));

        setPopularTabs(topLeaguesData);
      }
    } catch (error) {
      console.error("Error fetching top leagues per sport:", error);
      message.error(
        getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED),
      );
    } finally {
      setIsTableLoading(false);
      setLoading(false);
    }
  }, [PrivateAdminPanelInstance, activeSport, activePreMatchLive]);

  const handleAddItem = () => {
    setIsAddModalVisible(true);
  };

  const handleModalClose = () => {
    setIsAddModalVisible(false);
    setSearchValue("");
    setSelectedItem(null);
    setSearchOptions([]);
  };

  // Real search function using API
  const fetchSearchOptions = useMemo(() => {
    let timer;
    return (query) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(async () => {
        if (!query || query.length < 2 || !activeSport) {
          setSearchOptions([]);
          return;
        }
        setSearchLoading(true);
        try {
          const url = API_ROUTES.AUTOCOMPLETE_TOP_LEAGUES_PER_SPORT.replace(
            "{sport_id}",
            activeSport,
          )
            .replace("{event_type}", activePreMatchLive)
            .replace("{search_term}", encodeURIComponent(query));

          const response = await PrivateAdminPanelInstance.get(url);

          if (response.data.success) {
            const searchData = response.data.data.map((market) => ({
              entity_id: market.entity_id,
              market_id: market.market_id,
              name: market.name,
              display_name: market.display_name,
              country_name: market.country_name,
            }));
            setSearchOptions(searchData);
          }
        } catch (error) {
          console.error("Error searching markets:", error);
          message.error(
            getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED),
          );
        } finally {
          setSearchLoading(false);
        }
      }, 300);
    };
  }, [PrivateAdminPanelInstance, activeSport, activePreMatchLive]);

  useEffect(() => {
    fetchSports();
  }, [fetchSports]);

  useEffect(() => {
    fetchLiveSportsV2();
  }, [fetchLiveSportsV2]);

  useEffect(() => {
    fetchSearchOptions(searchValue);
  }, [searchValue, fetchSearchOptions]);

  const handleAddItemConfirm = async () => {
    if (!selectedItem || !activeSport) return;
    setAddLoading(true);

    try {
      const response = await PrivateAdminPanelInstance.post(
        API_ROUTES.CREATE_NEW_TOP_LEAGUES_PER_SPORT,
        {
          sport_id: activeSport,
          event_type: activePreMatchLive,
          item_id: selectedItem.entity_id.replace("LEAGUE#", ""),
          display_name: selectedItem.display_name,
          display_order: null,
          season_id: null,
        },
      );

      message.success(
        getApiSuccessMessage(
          response,
          AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED,
        ),
      );
      setIsAddModalVisible(false);
      setSearchValue("");
      setSelectedItem(null);
      setSearchOptions([]);

      // Refresh the top markets list
      await fetchLiveSportsV2();
    } catch (error) {
      console.error("Error adding league:", error);
      message.error(
        getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED),
      );
    } finally {
      setAddLoading(false);
    }
  };

  const handleItemSelect = (item) => {
    setSelectedItem(item);
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const toggleStatus = async (record) => {
    const previousState = [...popularTabs];
    const newStatus = !record.is_active;

    setIsTableLoading(true);

    try {
      const response = await PrivateAdminPanelInstance.patch(
        API_ROUTES.SET_LIVE_SPORTS_V2_ACTIVE,
        {
          entity_id: record.id,
          is_active: newStatus,
        },
      );

      // Optimistic update
      setPopularTabs((prevData) =>
        prevData.map((item) =>
          item.id === record.id ? { ...item, is_active: newStatus } : item,
        ),
      );

      message.success(
        getApiSuccessMessage(
          response,
          AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED,
        ),
      );
    } catch (error) {
      console.error("Error toggling league status:", error);
      message.error(
        getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED),
      );
      // Revert on error
      setPopularTabs(previousState);
    } finally {
      setIsTableLoading(false);
    }
  };

  const changeOrder = async (reorderedItems) => {
    setIsTableLoading(true);
    try {
      // Update local state first for immediate UI feedback
      setPopularTabs(reorderedItems);

      // Send API request to reorder
      const orderedIds = reorderedItems.map((item) => item.id);
      const response = await PrivateAdminPanelInstance.patch(
        API_ROUTES.ORDER_TOP_LEAGUES_PER_SPORT,
        {
          sport_id: activeSport,
          event_type: activePreMatchLive,
          ordered_ids: orderedIds,
        },
      );

      message.success(
        getApiSuccessMessage(
          response,
          AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED,
        ),
      );
    } catch (error) {
      console.error("Error reordering top leagues:", error);
      message.error(
        getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED),
      );
      // Revert to original state on error
      fetchLiveSportsV2();
    } finally {
      setIsTableLoading(false);
    }
  };

  // Rename handlers
  const handleRenameClick = (item, kind) => {
    setRenameTarget(item);
    setRenameKind(kind);
    setIsRenameModalVisible(true);
  };

  const handleRenameModalClose = () => {
    setIsRenameModalVisible(false);
    setRenameTarget(null);
    setRenameKind(null);
  };

  const handleRenameConfirm = async (name) => {
    if (!renameTarget) return;
    setRenameLoading(true);
    try {
      const response = await PrivateAdminPanelInstance.patch(
        API_ROUTES.RENAME_LIVE_SPORTS_V2,
        {
          entity_id: renameTarget.entity_id,
          display_name: name,
        },
      );

      message.success(
        getApiSuccessMessage(response, AppStrings.ITEM_RENAMED_SUCCESSFULLY),
      );

      const targetEntityId = renameTarget.entity_id;

      // Update table rows (top leagues)
      setPopularTabs((prevData) =>
        prevData.map((item) =>
          item.id === targetEntityId
            ? { ...item, ename: name }
            : item,
        ),
      );

      // Update sport pills (same rename modal; pills render sport.display_name)
      setSports((prev) =>
        prev.map((s) =>
          s.entity_id === targetEntityId
            ? {
                ...s,
                display_name: name,
                ename: name,
              }
            : s,
        ),
      );

      handleRenameModalClose();
    } catch (error) {
      console.error("Error renaming item:", error);
      message.error(
        getApiErrorMessage(error, AppStrings.FAILED_TO_RENAME_ITEM),
      );
    } finally {
      setRenameLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.emSportsHeaderContainer}>
        <div className={styles.emSportsList}>
          {sportsLoading ? (
            <Spin size="small" />
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleSportsDragEnd}
            >
              <SortableContext
                items={sports.map((s) => s.entity_id)}
                strategy={horizontalListSortingStrategy}
              >
                {sports.map((s) => (
                  <SortableSportPill
                    key={s.entity_id}
                    sport={s}
                    isActive={activeSport === s.sport_id}
                    onClick={() => setActiveSport(s.sport_id)}
                    disabled={!canEdit}
                    onRename={(sport) => handleRenameClick(sport, "sport")}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      <div className={styles.header}>
        <h2>{AppStrings.LIVE_SPORTS_V2}</h2>
        {canEdit && (
          <Button
            type="primary"
            icon={<Plus size={16} />}
            onClick={handleAddItem}
            className={styles.addButton}
          >
            {AppStrings.ADD_LEAGUE || "Add League"}
          </Button>
        )}
      </div>
      <div className={styles.tableContainer}>
        <SortingTable
          columns={["", "#Order", "Name", "Status", "Action", AppStrings.ORDER]}
          data={popularTabs}
          setData={setPopularTabs}
          toggleStatus={toggleStatus}
          onRename={(item) => handleRenameClick(item, "league")}
          changeOrder={changeOrder}
          isLoading={isTableLoading}
          canEdit={canEdit}
          enablePositionSelect
        />
      </div>

      <Modal
        closable={false}
        open={isAddModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={600}
        className="add-league-modal"
      >
        <div className="add-new-ip-panel">
          <div className="modal-header">
            <div className="modal-header-title">
              <h2 className="modal-title">
                {AppStrings.ADD_LEAGUE || "Add League"}
              </h2>
            </div>
            <X onClick={handleModalClose} className="close-icon" />
          </div>
          <div className="divider" />
          <div className="msb-modal-body">
            <div className="autocomplete">
              <Input
                placeholder={AppStrings.SEARCH_LEAGUES || "Search leagues..."}
                value={searchValue}
                onChange={handleSearchChange}
                disabled={!canEdit}
              />
              {searchValue ? (
                <div>
                  {searchLoading ? (
                    <div className="center">
                      <Spin size="small" />
                    </div>
                  ) : (
                    searchOptions?.map((option) => (
                      <div
                        key={option.entity_id}
                        className={`${styles.leagueCard} ${
                          selectedItem?.entity_id === option.entity_id
                            ? styles.selected
                            : ""
                        }`}
                        onClick={
                          canEdit ? () => handleItemSelect(option) : undefined
                        }
                        style={{ cursor: canEdit ? "pointer" : "not-allowed" }}
                      >
                        <div className={styles.leagueInfo}>
                          <span className={styles.leagueName}>
                            {option.display_name}
                          </span>
                        </div>
                        {option.country_name && (
                          <div className={styles.countryTag}>
                            <span className={styles.countryName}>
                              {option.country_name}
                            </span>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                  {!searchLoading &&
                    searchOptions.length === 0 &&
                    searchValue && (
                      <div className="empty-mini">
                        {AppStrings.NO_RESULTS_FOUND || "No results found"}
                      </div>
                    )}
                </div>
              ) : null}
            </div>
          </div>
          <div className="modal-footer">
            <button
              className="cancel-button"
              onClick={handleModalClose}
              disabled={addLoading || !canEdit}
            >
              {AppStrings.CLOSE}
            </button>
            <button
              className={`submit-button ${
                !selectedItem || addLoading || !canEdit ? "disabled" : ""
              }`}
              disabled={!selectedItem || addLoading || !canEdit}
              onClick={handleAddItemConfirm}
            >
              {addLoading ? AppStrings.ADDING : AppStrings.ADD_LEAGUE}
            </button>
          </div>
        </div>
      </Modal>

      <RenameEntityModal
        open={isRenameModalVisible}
        title={
          renameKind === "sport"
            ? AppStrings.RENAME_LIVE_SPORT
            : AppStrings.RENAME_LIVE_LEAGUE
        }
        nameLabel={AppStrings.DISPLAY_NAME}
        namePlaceholder={AppStrings.DISPLAY_NAME}
        initialName={renameTarget?.ename || renameTarget?.display_name || ""}
        saving={renameLoading}
        onSave={handleRenameConfirm}
        onClose={handleRenameModalClose}
      />
    </div>
  );
};

export default LiveSportsV2;
